import { FC, Fragment, useState, useRef, useEffect } from 'react';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import MovablePlaceholder from 'components/ui/MovablePlaceholder/MovablePlaceholder';
import Button from 'components/ui/Button/Button';
import { showWarning } from 'utils/utils';
import { useTranslation } from 'react-i18next';
import { SearchFlightModel } from 'ts/flight/classes/SearchFlightModel';
import { personsCount as persons } from 'constants/flight';
import { useClickAway } from 'hooks/useClickAway';
import withErrorBoundary from 'components/shared/withErrorBoundary';

interface ChoosePersonsProps {
  searchModel: SearchFlightModel;
  onChange: (value: SearchFlightModel) => void;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
}

const ChoosePersons: FC<ChoosePersonsProps> = ({ searchModel, onChange, ...props }) => {
  const [showPersons, setShowPersons] = useState(false);
  const isMountedRef = useRef(true);
  const personsRef = useClickAway(() => {
    if (isMountedRef.current) {
      setShowPersons(false);
    }
  });
  const [t] = useTranslation();
  const [infantsCount] = useState([...persons].slice(0, 3));
  const [personsCount] = useState([...persons]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const formatPersons = () => {
    let resultArr: string[] = [];
    if (searchModel.numberOfAdults) {
      const adultStr = searchModel.numberOfAdults > 1 ? t('search.adults') : t('search.adult');
      resultArr.push(`${searchModel.numberOfAdults} ${adultStr}`);
    }

    if (searchModel.numberOfChildren) {
      const childrenStr =
        searchModel.numberOfChildren > 1 ? t('search.children') : t('search.child');
      resultArr.push(`${searchModel.numberOfChildren} ${childrenStr}`);
    }

    if (searchModel.numberOfYouths) {
      const youthsStr = searchModel.numberOfYouths > 1 ? t('search.youths') : t('search.youth');
      resultArr.push(`${searchModel.numberOfYouths} ${youthsStr}`);
    }

    if (searchModel.numberOfSeniors) {
      const seniorsStr = searchModel.numberOfSeniors > 1 ? t('search.seniors') : t('search.senior');
      resultArr.push(`${searchModel.numberOfSeniors} ${seniorsStr}`);
    }

    if (searchModel.numberOfInfants) {
      const infantsStr = searchModel.numberOfInfants > 1 ? t('search.infants') : t('search.infant');
      resultArr.push(`${searchModel.numberOfInfants} ${infantsStr}`);
    }

    return resultArr.join(', ');
  };

  const onChangeCount = (value: SearchFlightModel) => {
    if (!isMountedRef.current) return;
    const maxPersons = 9;
    const personsSum =
      value.numberOfAdults + value.numberOfChildren + value.numberOfYouths + value.numberOfSeniors;

    if (personsSum > maxPersons) {
      showWarning(t('search.max_persons', { count: maxPersons }));
    } else if (value.numberOfInfants > value.numberOfAdults) {
      showWarning(t('search.infants_warning'));
    } else if (personsSum === maxPersons && value.numberOfInfants > 1) {
      showWarning(t('search.max_persons', { count: maxPersons }));
    } else {
      onChange(value);
    }
  };

  const handleShowPersons = () => {
    if (isMountedRef.current) {
      setShowPersons(true);
    }
  };

  const handleHidePersons = () => {
    if (isMountedRef.current) {
      setShowPersons(false);
    }
  };

  return (
    <Fragment>
      <div
        className={
          'form-control persons-control' +
          (props.movablePlaceholder ? ' movable-placeholder-input' : '')
        }
        onClick={handleShowPersons}
      >
        {formatPersons()}
      </div>
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={showPersons || !!formatPersons()}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {showPersons && (
        <div ref={personsRef} className="persons">
          <div className="persons-row">
            <span>{t('search.adults')} (12-64)</span>
            <SelectCustom
              options={personsCount}
              value={searchModel.numberOfAdults}
              onChange={(numberOfAdults) => onChangeCount({ ...searchModel, numberOfAdults })}
            />
          </div>
          <div className="persons-row">
            <span>{t('search.children')} (2-12)</span>
            <SelectCustom
              options={personsCount}
              value={searchModel.numberOfChildren}
              onChange={(numberOfChildren) => onChangeCount({ ...searchModel, numberOfChildren })}
            />
          </div>
          <div className="persons-row">
            <span>{t('search.youths')} (12-24)</span>
            <SelectCustom
              options={personsCount}
              value={searchModel.numberOfYouths}
              onChange={(numberOfYouths) => onChangeCount({ ...searchModel, numberOfYouths })}
            />
          </div>
          <div className="persons-row">
            <span>{t('search.seniors')} (65+)</span>
            <SelectCustom
              options={personsCount}
              value={searchModel.numberOfSeniors}
              onChange={(numberOfSeniors) => onChangeCount({ ...searchModel, numberOfSeniors })}
            />
          </div>
          <div className="persons-row">
            <span>{t('search.infants')} (0-2)</span>
            <SelectCustom
              options={infantsCount}
              value={searchModel.numberOfInfants}
              onChange={(numberOfInfants) => onChangeCount({ ...searchModel, numberOfInfants })}
            />
          </div>
          <div className="persons-row">
            <span></span>
            <Button
              className="btn btn-sm btn-primary"
              title={t('close')}
              onClick={handleHidePersons}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(ChoosePersons, 'ChoosePersons');
